import axios from "axios";
import config from "../config";
import { 
  GetCustomerDto, 
  GetCustomerDashboardDto, 
  GetApiKeyDto, 
  GetApiKeyConfigDto, 
  PostApiKeyConfigDto, 
  PostApiKeyDto,
  CountGroupedByDateDto
} from "./Dtos";
import { PaginatedResponse } from "./Dtos/PaginatedResponse";
import { CustomerDto } from "../components/sidebars/CustomerSidebar";
import { AddressGeocodeReverseEls } from "./Dtos/Els/AddressGeocodeReverseEls";
import { AddressGeocodeEls } from "./Dtos/Els/AddressGeocodeEls";
import { AddressValidateEls } from "./Dtos/Els/AddressValidateEls";
import { TopStatsDto } from "./Dtos/TopStatsDto";
import { PaginatedAddressResultDto } from "./Dtos/PaginatedAddressResultDto";
import { AddressRequestDto, AddressValidationResultDto } from "./Dtos/AddressRequestDto";
// content type
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.baseURL = config.API_URL;

export const CustomerService = {

  addresses: {
    list: (page: number = 1, pageSize: number = 20, addressStatus?: string, addressFromDate?: string, addressToDate?: string, addressInfo?: string) => {
      let url = `api/v1/dashboard/addresses?page=${page}&pageSize=${pageSize}`;
      if (addressStatus) url += `&addressStatus=${encodeURIComponent(addressStatus)}`;
      if (addressFromDate) url += `&addressFromDate=${encodeURIComponent(addressFromDate)}`;
      if (addressToDate) url += `&addressToDate=${encodeURIComponent(addressToDate)}`;
      if (addressInfo) url += `&addressInfo=${encodeURIComponent(addressInfo)}`;
      return axios.get<PaginatedAddressResultDto>(url);
    },
    getHistory: (traceId: string) => {
      return axios.get<AddressValidateEls[]>(`api/v1/dashboard/addresses/${traceId}`);
    },
    refreshValidationCache: (address: AddressRequestDto) => {
      return axios.post<AddressValidationResultDto>('api/v1/helpers/addresses/validate/cleanup-cache', address);
    }
  },

  customer: {
    getCustomers: () => {
      const fullUrl = `api/v1/dashboard/customers`; // Adjust the URL based on your API route
      return axios.get<GetCustomerDto[]>(fullUrl); // Specify the response type as an array of Company
    },

    getCustomer: (id: number) => {
      const fullUrl = `api/v1/dashboard/customers/${id}`; // Adjust the URL based on your API route
      return axios.get<GetCustomerDashboardDto>(fullUrl); // Specify the response type as Company
    },

    createCustomer: (customer: CustomerDto) => {
      return axios.post<GetCustomerDto>("api/v1/dashboard/customers", customer);
    },

    updateCustomer: (customerId: number, customer: CustomerDto) => {
      return axios.put<GetCustomerDto>(
        `api/v1/dashboard/customers/${customerId}`,
        customer
      );
    },
  },

  apiKey: {
    getApiKeyConfigs: (customerId: number, apiKeyId: number) => {
      return axios.get<GetApiKeyConfigDto[]>(
        `api/v1/dashboard/customers/${customerId}/apikeys/${apiKeyId}/configs`
      );
    },
    updateApiKeyConfigs: (
      customerId: number,
      apiKeyId: number,
      configs: PostApiKeyConfigDto[]
    ) => {
      return axios.post<GetApiKeyConfigDto[]>(
        `api/v1/dashboard/customers/${customerId}/apikeys/${apiKeyId}/configs`,
        configs
      );
    },

    getApiKeyById: (apiKeyId: number) => {
      const fullUrl = `api/v1/dashboard/apikeys/${apiKeyId}`;
      return axios.get<GetApiKeyDto>(fullUrl);
    },

    createApiKey: (apiKey: PostApiKeyDto) => {
      return axios.post<GetApiKeyDto>("api/v1/dashboard/apikeys", apiKey);
    },

    updateApiKey: (apiKeyId: number, apiKey: PostApiKeyDto) => {
      return axios.put<GetApiKeyDto>(
        `api/v1/dashboard/apikeys/${apiKeyId}`,
        apiKey
      );
    },

    toggleGemini: (apiKeyId: number) => {
      return axios.post<GetApiKeyDto>(
        `api/v1/dashboard/apikeys/${apiKeyId}/toggle-gemini`
      );
    },
  },

  requestAnalytics: {
    requests: {
      geocodeReverse: {
        byApiKeyId: (apiKeyId: number, startDate: string, endDate: string, processedBy: string, page: number = 1, pageSize: number = 100) => {
          return axios.get<PaginatedResponse<AddressGeocodeReverseEls>>(`api/v1/request-analytics/by-api-key-id/${apiKeyId}/geocode-reverse/requests?startDate=${startDate}&endDate=${endDate}&processedBy=${processedBy}&page=${page}&pageSize=${pageSize}`);
        },

        byCustomerId: (customerId: number, startDate: string, endDate: string, apiKeyId: number, page: number = 1, pageSize: number = 100) => {
          return axios.get<PaginatedResponse<AddressGeocodeReverseEls>>(`api/v1/request-analytics/by-customer-id/${customerId}/geocode-reverse/requests?startDate=${startDate}&endDate=${endDate}&apiKeyId=${apiKeyId}&page=${page}&pageSize=${pageSize}`);
        }
      },

      geocode: {
        byApiKeyId: (apiKeyId: number, startDate: string, endDate: string, processedBy: string, page: number = 1, pageSize: number = 100) => {
          return axios.get<PaginatedResponse<AddressGeocodeEls>>(`api/v1/request-analytics/by-api-key-id/${apiKeyId}/geocode/requests?startDate=${startDate}&endDate=${endDate}&processedBy=${processedBy}&page=${page}&pageSize=${pageSize}`);
        },

        byCustomerId: (customerId: number, startDate: string, endDate: string, apiKeyId: number, page: number = 1, pageSize: number = 100) => {
          return axios.get<PaginatedResponse<AddressGeocodeEls>>(`api/v1/request-analytics/by-customer-id/${customerId}/geocode/requests?startDate=${startDate}&endDate=${endDate}&apiKeyId=${apiKeyId}&page=${page}&pageSize=${pageSize}`);
        }
      },

      addressValidate: {
        byApiKeyId: (apiKeyId: number, startDate: string, endDate: string, processedBy: string, page: number = 1, pageSize: number = 100) => {
          return axios.get<PaginatedResponse<AddressValidateEls>>(`api/v1/request-analytics/by-api-key-id/${apiKeyId}/address-validate/requests?startDate=${startDate}&endDate=${endDate}&processedBy=${processedBy}&page=${page}&pageSize=${pageSize}`);
        },

        byCustomerId: (customerId: number, startDate: string, endDate: string, apiKeyId: number, page: number = 1, pageSize: number = 100) => {
          return axios.get<PaginatedResponse<AddressValidateEls>>(`api/v1/request-analytics/by-customer-id/${customerId}/address-validate/requests?startDate=${startDate}&endDate=${endDate}&apiKeyId=${apiKeyId}&page=${page}&pageSize=${pageSize}`);
        }
      }
    },

    topStats: {
      all: () => {
        return axios.get<TopStatsDto>(`api/v1/request-analytics/stats`);
      },

      byApiKeyId: (apiKeyId: number) => {
        return axios.get<TopStatsDto>(`api/v1/request-analytics/by-api-key-id/${apiKeyId}/stats`);
      },

      byCustomerId: (customerId: number) => {
        return axios.get<TopStatsDto>(`api/v1/request-analytics/by-customer-id/${customerId}/stats`);
      }
    },

    stats: {
      grouped: (startDateTime: string, endDateTime: string, groupByScale: string, apiKeyId?: number, customerId?: number, statsType?: string) => {
        let url = `api/v1/request-analytics/stats/grouped?startDateTime=${startDateTime}&endDateTime=${endDateTime}&groupByScale=${groupByScale}`;
        if (apiKeyId) url += `&apiKeyId=${apiKeyId}`;
        if (customerId) url += `&customerId=${customerId}`;
        if (statsType) url += `&statsType=${statsType}`;
        return axios.get<CountGroupedByDateDto[]>(url);
      }
    },

    maps: {
      analytics: {
        geocodeReverse: (startDateTime: string, endDateTime: string, customerId?: number, apiKeyId?: number, processedBy?: string, page?: number, pageSize?: number) => {
          let url = `api/v1/maps/geocode-reverse/requests?startDateTime=${startDateTime}&endDateTime=${endDateTime}&page=${page}&pageSize=${pageSize}`;
          if (customerId) url += `&customerId=${customerId}`;
          if (apiKeyId) url += `&apiKeyId=${apiKeyId}`;
          if (processedBy) url += `&processedBy=${processedBy}`;
          return axios.get<any[]>(url);
        },

        // geocode/requests
        geocode: (startDateTime: string, endDateTime: string, customerId?: number, apiKeyId?: number, processedBy?: string, page?: number, pageSize?: number) => {
          let url = `api/v1/maps/geocode/requests?startDateTime=${startDateTime}&endDateTime=${endDateTime}&page=${page}&pageSize=${pageSize}`;
          if (customerId) url += `&customerId=${customerId}`;
          if (apiKeyId) url += `&apiKeyId=${apiKeyId}`;
          if (processedBy) url += `&processedBy=${processedBy}`;
          return axios.get<any[]>(url);
        },

        addressValidate: (startDateTime: string, endDateTime: string, customerId?: number, apiKeyId?: number, processedBy?: string, addressStatus?: string, page?: number, pageSize?: number) => {
          let url = `api/v1/maps/address-validate/requests?startDateTime=${startDateTime}&endDateTime=${endDateTime}&page=${page}&pageSize=${pageSize}`;
          if (customerId) url += `&customerId=${customerId}`;
          if (apiKeyId) url += `&apiKeyId=${apiKeyId}`;
          if (processedBy) url += `&processedBy=${processedBy}`;
          if (addressStatus) url += `&addressStatus=${addressStatus}`;
          return axios.get<any[]>(url);
        }
      }
    }
  }
};
